import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface GuestAddress {
  latitude: number;
  longitude: number;
  formatted: string;
  /**
   * Comment ce point a été obtenu — et donc à quel point on peut s'y fier
   * pour livrer.
   *
   *   picked   le client l'a désigné lui-même (recherche, carte, adresse
   *            enregistrée choisie dans la liste)
   *   adopted  repris automatiquement de ses adresses enregistrées, faute de
   *            géolocalisation. Bonne hypothèse, mais personne ne l'a validée.
   *   device   position du navigateur, jamais confirmée
   *
   * Le panier s'en sert pour décider s'il faut faire confirmer le lieu avant
   * de commander. Absent sur les points enregistrés avant cette distinction :
   * traité comme non désigné, donc à faire confirmer — le sens prudent.
   */
  origin?: "picked" | "adopted" | "device";
}

interface AddressState {
  selectedAddressUlid: string | null;
  setSelectedAddressUlid: (ulid: string | null) => void;
  /** Address chosen by a guest (no account). Persisted + reactive so the
   *  store list / carousels refetch for the new location everywhere. */
  guestAddress: GuestAddress | null;
  setGuestAddress: (addr: GuestAddress | null) => void;
  /**
   * Point de livraison validé de visu par le client, sur la carte du panier,
   * sous la forme « lat,lng » arrondie à ~1 m.
   *
   * Persisté pour ne pas reposer la question à chaque visite du panier, mais
   * lié aux coordonnées : changer d'adresse invalide la confirmation et la
   * question revient — c'est précisément le cas où il faut redemander.
   */
  confirmedDeliveryKey: string | null;
  setConfirmedDeliveryKey: (key: string | null) => void;
}

/** Clé de confirmation d'un point de livraison. */
export function deliveryKey(coords: { latitude: number; longitude: number }): string {
  return `${coords.latitude.toFixed(5)},${coords.longitude.toFixed(5)}`;
}

export const useAddressStore = create<AddressState>()(
  persist(
    (set) => ({
      selectedAddressUlid: null,
      setSelectedAddressUlid: (ulid) => set({ selectedAddressUlid: ulid }),
      guestAddress: null,
      setGuestAddress: (addr) =>
        set((state) => ({
          guestAddress: addr,
          // Un point que le client n'a pas désigné ne peut pas hériter d'une
          // confirmation donnée pour un autre : on la retire.
          confirmedDeliveryKey:
            addr && addr.origin === "picked" ? state.confirmedDeliveryKey : null,
        })),
      confirmedDeliveryKey: null,
      setConfirmedDeliveryKey: (key) => set({ confirmedDeliveryKey: key }),
    }),
    { name: "fast-address" }
  )
);
